import { colors, headingFont, uiFont } from '../theme';
import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Chip, Paper, Skeleton, Typography } from '@mui/material';
import {
  getProductDisplayName,
  getProductImageSrcSet,
  getProductImageUrl,
  getProductPriceLabel,
  getProductPath,
} from '../utils/product';

export const TOP_SELECTION_LIMIT = 10;
const TOP_SELECTION_SKELETONS = Array.from(
  { length: TOP_SELECTION_LIMIT },
  (_, index) => index
);

function SelectionSkeletonItem() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '24px 64px minmax(0, 1fr)',
          lg: '20px 48px minmax(0, 1fr)',
        },
        alignItems: 'center',
        gap: 1,
        minWidth: { xs: 252, lg: 0 },
        p: { xs: 1, lg: '4px 0' },
        border: { xs: '1px solid rgba(255,255,255,0.06)', lg: 0 },
        borderBottom: { lg: '1px solid rgba(255,255,255,0.04)' },
        '&:last-child': { borderBottom: { lg: 0 } },
        borderRadius: { xs: 3, lg: 0 },
        backgroundColor: colors.surface,
      }}
    >
      <Skeleton animation="wave" width={20} height={20} />
      <Skeleton
        animation="wave"
        variant="rounded"
        sx={{ width: { xs: 64, lg: 48 }, height: { xs: 64, lg: 48 } }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Skeleton animation="wave" width="92%" sx={{ height: { xs: 20, lg: 16 } }} />
        <Skeleton animation="wave" width="72%" sx={{ height: { xs: 16, lg: 12 } }} />
        <Skeleton animation="wave" width="42%" sx={{ height: { xs: 18, lg: 14 } }} />
      </Box>
    </Box>
  );
}

function TopSelection({
  title = 'Top seleccion',
  products = [],
  loading = false,
  priorityFirstImage = false,
  onProductSelect,
  selectedMediaType = 'vinyl',
  onMediaTypeChange,
}) {
  const showMediaTypeSelector = typeof onMediaTypeChange === 'function';

  return (
    <Paper
      component="aside"
      elevation={0}
      aria-labelledby="top-selection-title"
      sx={{
        p: { xs: 1.5, sm: 2, lg: 1.5 },
        borderRadius: 1,
        border: '1px solid rgba(255,255,255,0.06)',
        backgroundColor: colors.surface,
        boxShadow: '0 16px 34px rgba(0,0,0,0.045)',
        overflow: 'hidden',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: colors.accent,
          fontWeight: 900,
          letterSpacing: 1.2,
          fontSize: { xs: '0.72rem', lg: '0.66rem' },
        }}
      >
        SELECCION DISCOXDISCOS
      </Typography>

      <Typography
        id="top-selection-title"
        component="h2"
        sx={{
          fontFamily: headingFont,
          mt: -0.2,
          mb: { xs: 1.25, lg: 0.6 },
          color: colors.text,
          fontWeight: 400,
          fontSize: { xs: '1.5rem', lg: '1.45rem' },
          lineHeight: 1.05,
        }}
      >
        {title}
      </Typography>

      {showMediaTypeSelector && (
        <Box
          aria-label="Elegir ranking por soporte"
          sx={{
            display: 'flex',
            gap: 0.6,
            mb: { xs: 1.25, lg: 0.75 },
          }}
        >
          {[
            { value: 'vinyl', label: 'Vinilos' },
            { value: 'cd', label: 'CDs' },
          ].map((option) => {
            const selected = selectedMediaType === option.value;

            return (
              <Chip
                key={option.value}
                label={option.label}
                clickable
                aria-pressed={selected}
                onClick={() => onMediaTypeChange(option.value)}
                size="small"
                sx={{
                  height: { xs: 28, lg: 24 },
                  borderRadius: 1,
                  border: `1px solid ${selected ? colors.text : colors.controlBorder}`,
                  backgroundColor: selected ? colors.button : colors.surface,
                  color: selected ? colors.white : colors.text,
                  fontWeight: 800,
                  '& .MuiChip-label': {
                    px: { xs: 1.1, lg: 0.9 },
                    fontSize: { xs: '0.72rem', lg: '0.64rem' },
                  },
                  '&:hover': {
                    backgroundColor: selected
                      ? colors.hover
                      : colors.tint,
                    borderColor: colors.accent,
                  },
                }}
              />
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          display: { xs: 'flex', lg: 'block' },
          gap: { xs: 1, lg: 0 },
          overflowX: { xs: 'auto', lg: 'visible' },
          scrollSnapType: { xs: 'x mandatory', lg: 'none' },
          pb: { xs: 0.75, lg: 0 },
          pr: { xs: 0.5, lg: 0 },
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-track': {
            backgroundColor: colors.elevated,
            borderRadius: 10,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.controlBorder,
            borderRadius: 10,
          },
        }}
      >
        {loading
          ? TOP_SELECTION_SKELETONS.map((item) => (
              <SelectionSkeletonItem key={`top-selection-skeleton-${item}`} />
            ))
          : products.slice(0, TOP_SELECTION_LIMIT).map((product, index) => {
              const title = getProductDisplayName(product);
              const metadata =
                [product.label, product.format || product.style]
                  .filter(Boolean)
                  .join(' • ') || 'Sin datos extra';

              return (
                <Box
                  key={product._id}
                  component={onProductSelect ? 'button' : Link}
                  to={onProductSelect ? undefined : getProductPath(product)}
                  type={onProductSelect ? 'button' : undefined}
                  onClick={
                    onProductSelect ? () => onProductSelect(product) : undefined
                  }
                  aria-label={
                    onProductSelect ? `Ver detalle de ${title}` : `Ver ${title}`
                  }
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '24px 64px minmax(0, 1fr)',
                      lg: '20px 48px minmax(0, 1fr)',
                    },
                    alignItems: 'center',
                    gap: 1,
                    minWidth: { xs: 252, lg: 0 },
                    p: { xs: 1, lg: '4px 0' },
                    color: 'inherit',
                    font: 'inherit',
                    textAlign: 'left',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    width: '100%',
                    border: { xs: '1px solid rgba(255,255,255,0.06)', lg: 0 },
                    borderBottom: { lg: '1px solid rgba(255,255,255,0.04)' },
                    '&:last-child': { borderBottom: { lg: 0 } },
                    borderRadius: { xs: 3, lg: 0 },
                    backgroundColor: colors.surface,
                    scrollSnapAlign: 'start',
                    transition: 'background-color 0.18s ease, transform 0.18s ease',
                    '&:hover': {
                      backgroundColor: colors.tint,
                      transform: { xs: 'none', lg: 'translateX(2px)' },
                    },
                    '&:focus-visible': {
                      outline: '2px solid var(--dxd-focus)',
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Typography
                    aria-hidden="true"
                    sx={{
                      color: colors.accent,
                      fontWeight: 900,
                      fontSize: { xs: '0.78rem', lg: '0.7rem' },
                      fontFamily: uiFont,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </Typography>

                  <Box
                    component="img"
                    src={getProductImageUrl(product.image, 'thumb')}
                    srcSet={getProductImageSrcSet(product.image, 'thumb')}
                    sizes="(min-width: 1200px) 48px, 64px"
                    alt=""
                    width={64}
                    height={64}
                    loading={priorityFirstImage && index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={
                      priorityFirstImage && index === 0 ? 'high' : undefined
                    }
                    decoding="async"
                    sx={{
                      width: { xs: 64, lg: 48 },
                      height: { xs: 64, lg: 48 },
                      borderRadius: 0.5,
                      objectFit: 'cover',
                      backgroundColor: colors.elevated,
                    }}
                  />

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="h3"
                      sx={{
                        fontFamily: headingFont,
                        textTransform: 'uppercase',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        color: colors.text,
                        fontWeight: 400,
                        fontSize: { xs: '0.95rem', lg: '0.9rem' },
                        lineHeight: { xs: 1.12, lg: 1.05 },
                        wordBreak: 'break-word',
                      }}
                    >
                      {title}
                    </Typography>

                    <Typography
                      sx={{
                        mt: { xs: 0.35, lg: 0.2 },
                        color: colors.secondary,
                        fontSize: { xs: '0.66rem', lg: '0.58rem' },
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textTransform: 'uppercase',
                      }}
                    >
                      {metadata}
                    </Typography>

                    <Typography
                      sx={{
                        mt: { xs: 0.35, lg: 0.25 },
                        color: colors.text,
                        fontWeight: 900,
                        fontSize: { xs: '0.78rem', lg: '0.7rem' },
                        lineHeight: 1,
                      }}
                    >
                      {getProductPriceLabel(product)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
      </Box>

      {!loading && !products.length && (
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px dashed var(--dxd-border)',
            backgroundColor: colors.surface,
          }}
        >
          <Typography sx={{ color: colors.secondary, fontSize: '0.82rem' }}>
            Todavía no hay productos para esta selección.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default TopSelection;
